import React, { FC, Fragment } from 'react';
import {
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Paper,
    Button,
    Box,
    IconButton,
    Tooltip,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Organization } from 'models/organization';
import FormattedAddress from 'components/shared/address/formatted-address.component';
import DeleteIcon from '@mui/icons-material/Delete';
import InfiniteScroll from 'react-infinite-scroll-component';
import useFixMissingScroll from 'utils/use-fix-missing-scroll';
import LabDetailsDialog from './lab-details-dialog/lab-details-dialog.component';

type ConnectToLabListProps = {
    labs: Organization[];
    showConnected?: boolean;
    onFetchMoreChange: () => void;
    onConnectClick?: (labId: string) => void;
    onDisconnectClick?: (labId: string) => void;
};
const ConnectToLabList: FC<ConnectToLabListProps> = ({
    labs,
    showConnected,
    onFetchMoreChange,
    onConnectClick,
    onDisconnectClick,
}) => {
    const { t: translate } = useTranslation(['common', 'organization', 'lab-settings']);
    const [isDialogOpen, setIsDialogOpen] = React.useState(false);
    const [selectedLab, setSelectedLab] = React.useState<Organization>();

    const workingDays = (lab: Organization) => {
        const daysOfWeek = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'];
        const activeDays = lab.openingHours.filter((oh) => oh.active).map((oh) => oh.day.toUpperCase());
        const ranges: string[] = [];
        let start = activeDays[0];
        let prev = activeDays[0];

        for (let i = 1; i <= activeDays.length; i++) {
            if (i === activeDays.length || daysOfWeek.indexOf(activeDays[i]) !== daysOfWeek.indexOf(prev) + 1) {
                const translatedStart = translate(`lab-settings:${start}`);
                const translatedPrev = translate(`lab-settings:${prev}`);
                ranges.push(start === prev ? translatedStart : `${translatedStart}-${translatedPrev}`);
                start = activeDays[i];
            }
            prev = activeDays[i];
        }

        return ranges.join(', ');
    };

    const handleDialogClose = () => {
        setIsDialogOpen(false);
        setSelectedLab(undefined);
    };

    const handleRowClick = (lab: Organization) => {
        setSelectedLab(lab);
        setIsDialogOpen(true);
    };

    // Fix for infinite scroll not working on some screen sizes.
    useFixMissingScroll(onFetchMoreChange, labs?.length || 0);

    return (
        <Fragment>
            <InfiniteScroll
                dataLength={labs.length}
                next={onFetchMoreChange}
                hasMore={true}
                throttleTimeInMs={75}
                loader={undefined}
            >
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell width={170}></TableCell>
                                <TableCell width={300}>{translate('organization:LAB_NAME')}</TableCell>
                                <TableCell>{translate('common:DESCRIPTION')}</TableCell>
                                <TableCell width={180}>{translate('organization:WORKING_DAYS')}</TableCell>
                                <TableCell>{translate('organization:CONTACT_INFO')}</TableCell>
                                <TableCell width={230}></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {labs.map((lab: Organization) => (
                                <TableRow
                                    key={lab._id}
                                    hover
                                    onClick={() => handleRowClick(lab)}
                                    sx={{ cursor: 'pointer' }}
                                >
                                    <TableCell align="center">
                                        {lab.logoSignedUrl ? (
                                            <img
                                                src={lab.logoSignedUrl}
                                                alt={lab.name}
                                                style={{ maxWidth: 170, maxHeight: 60 }}
                                            />
                                        ) : undefined}
                                    </TableCell>
                                    <TableCell>{lab.name}</TableCell>
                                    <TableCell>{lab.shortDescription}</TableCell>
                                    <TableCell>{workingDays(lab)}</TableCell>
                                    <TableCell>
                                        {lab.contact?.address ? (
                                            <Box>
                                                <FormattedAddress
                                                    address={lab.contact?.address}
                                                    oneLineFormat
                                                    hideCountry
                                                />
                                            </Box>
                                        ) : undefined}
                                        {lab.contact.communication?.phone ? (
                                            <Box>
                                                {translate('common:PHONE')}: {lab.contact.communication.phone}
                                            </Box>
                                        ) : undefined}
                                    </TableCell>
                                    <TableCell align="right">
                                        <Button></Button>
                                        {showConnected ? (
                                            <Tooltip title={<span>{translate('organization:REMOVE_CONNECTION')}</span>}>
                                                <IconButton
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        if (onDisconnectClick) {
                                                            onDisconnectClick(lab._id);
                                                        }
                                                    }}
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            </Tooltip>
                                        ) : (
                                            <Button
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    if (onConnectClick) {
                                                        onConnectClick(lab._id);
                                                    }
                                                }}
                                            >
                                                {translate('organization:CONNECT_TO_LAB')}
                                            </Button>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </InfiniteScroll>

            <LabDetailsDialog
                lab={selectedLab}
                onClose={handleDialogClose}
                open={isDialogOpen}
                isConnected={showConnected}
            />
        </Fragment>
    );
};

export default ConnectToLabList;
